import React, { useState } from 'react';
import axios from 'axios';
import config from '../../../config';
import { toast } from '../../../components/Common/Toast';
import InterviewSetupModal from '../../../components/Teams/interview/InterviewSetupModal';
import MilitaryAssessmentSetupModal from '../../../components/Teams/assessment/MilitaryAssessmentSetupModal';
import './AddMemberOptionsModal.css';

const AddMemberOptionsModal = ({
  show, onClose, teamId, teamName, teamType,
  friends, teamMembers, onAddMember, onStartInterview,
  onStartMilitaryProgram, token, user
}) => {
  const [selectedMode, setSelectedMode] = useState(null);
  // Always use multi‑select (no more selectedFriend for any team type)
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showMilitaryProgramModal, setShowMilitaryProgramModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const isMilitaryTeam = teamType === 'military';

  const availableFriends = friends.filter(friend => {
    const notInTeam = !teamMembers?.some(member => member.user_id === friend.user_id);
    const matchesSearch = searchQuery === '' ||
      `${friend.first_name} ${friend.last_name}`.toLowerCase().includes(searchQuery.toLowerCase());
    return notInTeam && matchesSearch;
  });

  // Toggle multi‑selection (works for all modes)
  const toggleFriendSelection = (friend) => {
    setSelectedFriends(prev => {
      const exists = prev.find(f => f.user_id === friend.user_id);
      return exists ? prev.filter(f => f.user_id !== friend.user_id) : [...prev, friend];
    });
  };

  // Direct add (now supports multi‑selection for all teams)
const handleDirectAdd = async () => {
  if (selectedFriends.length === 0) {
    toast.error('Please select at least one friend');
    return;
  }

  setLoading(true);

  try {
    const userIds = selectedFriends.map(friend => friend.user_id);

    await onAddMember(userIds);

    toast.success(`Added ${selectedFriends.length} member(s) successfully`);
    resetAndClose();
  } catch {
    toast.error('Failed to add members');
  } finally {
    setLoading(false);
  }
};

  // Start interview flow – now always multi‑candidate
  const handleStartInterviewFlow = () => {
    if (selectedFriends.length === 0) {
      toast.error('Please select at least one candidate');
      return;
    }
    setShowInterviewModal(true);
  };

  // Start military program (unchanged)
  const handleStartMilitaryProgram = () => {
    if (selectedFriends.length === 0) return toast.error('Please select at least one candidate');
    setShowMilitaryProgramModal(true);
  };

  // Callback from InterviewSetupModal – sends array of candidates
  const handleStartInterview = async (interviewConfig) => {
    if (selectedFriends.length === 0 || loading) return;
    setLoading(true);
    try {
      // Build candidates array with user IDs
      const candidates = selectedFriends.map(f => ({
        user_id: f.user_id,
        candidate_name: `${f.first_name} ${f.last_name}`
      }));

      await onStartInterview({
        ...interviewConfig,
        candidates: candidates.map(c => c.user_id),  // array of user_ids
        candidate_names: candidates.map(c => c.candidate_name)
      });
      setShowInterviewModal(false);
      resetAndClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start interview');
    } finally {
      setLoading(false);
    }
  };


const handleStartMilitaryAssessment = async (formData) => {
  console.log("form data received:", formData);
  
  // ✅ Extract the candidates array from the form data
  const candidatesArray = formData.candidates || [];
  const isBatchAssessment = candidatesArray.length > 1;
  
  try {
    // Format candidates for the API
    const formattedCandidates = candidatesArray.map(c => ({
      user_id: c.user_id,
      candidate_name: c.candidate_name || c.first_name ? `${c.first_name || ''} ${c.last_name || ''}`.trim() : null
    }));
    
    const response = await axios.post(
      `${config.baseURL}/api/military/teams/${teamId}/start-program`,
      { 
        config: { 
          candidates: formattedCandidates,
          batch_id: `batch_${Date.now()}`,
          is_batch_assessment: isBatchAssessment,
          mode: formData.mode || 'video',
          capture: formData.capture || ['camera', 'voice'],
          language: formData.language || 'english',
          week_number: formData.week_number || 1
        } 
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.success) {
      const assessment = response.data.assessment;
      
      // ✅ Pass the assessment data to the parent handler
      onStartMilitaryProgram({
        isGroupInterview: isBatchAssessment,
        batchId: assessment.batch_id,
        assessment: assessment,
        candidates: assessment.candidates || formattedCandidates.map((c, i) => ({
          user_id: c.user_id,
          first_name: candidatesArray[i]?.candidate_name?.split(' ')[0] || '',
          last_name: candidatesArray[i]?.candidate_name?.split(' ').slice(1).join(' ') || '',
          soldier_id: assessment.candidates?.[i]?.soldier_id
        })),
        sessions: assessment.sessions || [],
        soldierIds: assessment.candidates?.map(c => c.soldier_id) || [],
        questions: assessment.questions || [],
        session: {
          session_id: assessment.batch_id,
          week_number: 1,
          program_id: null
        }
      });
      
      toast.success(`Military assessment started for ${formattedCandidates.length} soldier(s)`);
    }
  } catch (error) {
    console.error("Error starting military assessment:", error);
    toast.error(error.response?.data?.message || 'Failed to start assessment');
  }
};

  const resetAndClose = () => {
    setSelectedMode(null);
    setSelectedFriends([]);
    setSearchQuery('');
    setLoading(false);
    onClose();
  };

  const isProcessing = loading;
  if (!show) return null;

  return (
    <>
      <div className="add-member-overlay" onClick={isProcessing ? undefined : resetAndClose}>
        <div className="add-member-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Add Member to {teamName}</h2>
            <button className="close-btn" onClick={resetAndClose} disabled={isProcessing}>&times;</button>
          </div>

          <div className="modal-body">
            {!selectedMode ? (
              <div className="mode-selection">
                <h3>Choose how to add</h3>
                <div className="mode-grid">
                  <div className={`mode-card ${isProcessing ? 'disabled' : ''}`}
                       onClick={() => !isProcessing && setSelectedMode('direct')}>
                    <div className="mode-icon">➕</div>
                    <h4>Add</h4>
                    <p>Add immediately to team</p>
                  </div>
                  <div className={`mode-card ${isProcessing ? 'disabled' : ''}`}
                       onClick={() => !isProcessing && setSelectedMode(isMilitaryTeam ? 'military' : 'interview')}>
                    <div className="mode-icon">{isMilitaryTeam ? '📝' : '🎤'}</div>
                    <h4>{isMilitaryTeam ? 'Military Assessment' : 'Interview'}</h4>
                    <p>{isMilitaryTeam ? 'Start 6-week assessment program' : 'Start interview process'}</p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="friend-selection">
                  <div className="search-container">
                    <input type="text" className="interview-search-input"
                           placeholder="Search friends..." value={searchQuery}
                           onChange={(e) => setSearchQuery(e.target.value)} disabled={isProcessing} />
                  </div>

                  {/* Multi‑select header – shown for all modes now */}
                  <div className="amom-selection-header">
                    <span className="selected-count">{selectedFriends.length} selected</span>
                    {selectedFriends.length > 0 && (
                      <button className="clear-selection" onClick={() => setSelectedFriends([])}
                              disabled={isProcessing}>Clear</button>
                    )}
                  </div>

                  {availableFriends.length === 0 ? (
                    <div className="empty-state">
                      <p>No friends available to add</p>
                      <p className="empty-state-subtitle">You need to have friends to add them to your team</p>
                    </div>
                  ) : (
                    <div className="friends-list">
                      {availableFriends.map(friend => {
                        const isSelected = selectedFriends.some(f => f.user_id === friend.user_id);
                        return (
                          <div key={friend.user_id}
                               className={`friend-item ${isSelected ? 'selected' : ''} ${isProcessing ? 'disabled' : ''}`}
                               onClick={() => !isProcessing && toggleFriendSelection(friend)}>
                            {/* Checkbox always visible */}
                            <div className="friend-checkbox">
                              <input type="checkbox" checked={isSelected} onChange={() => {}} disabled={isProcessing} />
                            </div>
                            <img src={friend.profile_image ? `${config.baseURL}/uploads/profiles/${friend.profile_image}` : '/images/no-image-found.webp'}
                                 alt={friend.first_name}
                                 onError={(e) => { e.target.src = '/images/no-image-found.webp'; }} />
                            <div className="friend-details">
                              <strong>{friend.first_name} {friend.last_name}</strong>
                              <span>{friend.profession || 'No profession'}</span>
                              {friend.designation && <small>{friend.designation}</small>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Info messages – show for interview mode */}
                  {!isMilitaryTeam && selectedMode === 'interview' && selectedFriends.length > 0 && (
                    <div className="info-message">
                      <span className="info-icon">ℹ️</span>
                      <p>You'll be able to configure interview settings in the next step. The selected candidates will receive an invitation to join.</p>
                    </div>
                  )}
                  {isMilitaryTeam && selectedFriends.length > 0 && (
                    <div className="info-message">
                      <span className="info-icon">ℹ</span>
                      <p>The candidates will go through a 6-week assessment program with weekly sessions and AI-powered role matching.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer buttons – adjusted for multi‑select */}
          {selectedMode && selectedFriends.length > 0 && (
            <div className="modal-footer">
              <button className="confirm-btn bg-light text-black border"
                      onClick={() => { setSelectedMode(null); setSelectedFriends([]); }}
                      disabled={isProcessing}>Back</button>
              <button className="confirm-btn"
                      onClick={
                        selectedMode === 'direct' ? handleDirectAdd :
                        selectedMode === 'military' ? handleStartMilitaryProgram :
                        handleStartInterviewFlow
                      }
                      disabled={isProcessing}>
                {isProcessing ? (
                  <><span className="spinner-small"></span>Processing...</>
                ) : (
                  selectedMode === 'direct'
                    ? `Add ${selectedFriends.length} Member(s)`
                    : selectedMode === 'military'
                      ? `Start Assessment (${selectedFriends.length})`
                      : `Continue to Interview Setup (${selectedFriends.length})`
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <InterviewSetupModal
        show={showInterviewModal}
        onClose={() => setShowInterviewModal(false)}
        onStart={handleStartInterview}
        teamName={teamName}
        selectedFriends={selectedFriends}    // <-- pass array
      />
      <MilitaryAssessmentSetupModal
        show={showMilitaryProgramModal}
        onClose={() => setShowMilitaryProgramModal(false)}
        onStart={handleStartMilitaryAssessment}
        teamName={teamName}
        selectedMembers={selectedFriends}
        weekNumber={1}
      />
    </>
  );
};

export default AddMemberOptionsModal;